import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, Save, Calculator } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertEmails } from "@/components/AlertEmails";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setForm(data)).catch(() => {}); }, []);
  if (!form) return <div className="py-20 text-center text-slate-400">Carregando...</div>;

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const wh = Number(form.worked_hours || 0);
  const tf = wh ? ((Number(form.accidents_with_leave || 0) * 1e6) / wh).toFixed(2) : "0.00";
  const tg = wh ? ((Number(form.lost_days || 0) * 1e6) / wh).toFixed(2) : "0.00";

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        last_accident_date: form.last_accident_date || null,
        counter_start_date: form.counter_start_date || null,
        tf_target: Number(form.tf_target || 0),
        tg_target: Number(form.tg_target || 0),
        dds_planned_month: Number(form.dds_planned_month || 0),
        worked_hours: Number(form.worked_hours || 0),
        accidents_with_leave: Number(form.accidents_with_leave || 0),
        lost_days: Number(form.lost_days || 0),
      });
      toast.success("Configurações salvas");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const field = (key, label, type = "number", help) => (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</Label>
      <Input type={type} value={form[key] ?? ""} onChange={(e) => setF(key, e.target.value)} data-testid={`settings-${key}`} />
      {help && <p className="mt-1 text-xs text-slate-400">{help}</p>}
    </div>
  );

  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <PageHeader icon={SettingsIcon} title="Configurações SSMA" subtitle="Metas, placar de segurança e indicadores NBR 14280 (TF / TG)">
        <Button onClick={save} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="settings-save"><Save className="mr-1.5 h-4 w-4" /> {saving ? "Salvando..." : "Salvar"}</Button>
      </PageHeader>

      <div className="space-y-6">
        <AlertEmails />
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-[#134074]">Placar & Metas</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {field("counter_start_date", "Data de início do contador (definir 1x)", "date", "A partir desta data o placar conta sozinho")}
            {field("last_accident_date", "Data do último acidente c/ afastamento", "date", "Reinicia o contador (use 'Registrar acidente' no Dashboard)")}
            {field("dds_planned_month", "DDS programados por mês")}
            {field("tf_target", "Meta Taxa de Frequência (TF)")}
            {field("tg_target", "Meta Taxa de Gravidade (TG)")}
          </div>
        </div>

        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <h3 className="mb-1 text-base font-semibold text-[#134074]">Indicadores de acidentes (NBR 14280 / Braskem)</h3>
          <p className="mb-4 text-xs text-slate-400">Dados exigidos nas planilhas de SSMA do cliente para cálculo de TF e TG.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {field("accidents_with_leave", "Acidentes COM afastamento")}
            {field("lost_days", "Dias perdidos + debitados")}
            {field("worked_hours", "Horas de exposição/trabalhadas")}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-[#0B2545] p-4 text-white">
              <div className="flex items-center gap-1.5 text-xs text-emerald-300"><Calculator className="h-3.5 w-3.5" /> TF calculada</div>
              <p className="mt-1 text-2xl font-extrabold">{tf}</p>
              <p className="text-[10px] text-slate-300">(acidentes × 10⁶) / horas</p>
            </div>
            <div className="rounded-lg bg-[#134074] p-4 text-white">
              <div className="flex items-center gap-1.5 text-xs text-emerald-300"><Calculator className="h-3.5 w-3.5" /> TG calculada</div>
              <p className="mt-1 text-2xl font-extrabold">{tg}</p>
              <p className="text-[10px] text-slate-300">(dias perdidos × 10⁶) / horas</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
